import type { PeopleResponse, PersonRef, ServiceForOrgRef, ServicesForOrgResponse, TicketFormOptions } from '@central/contracts';
import { get } from '@/lib/api';

/**
 * Acesso às consultas de apoio (`/lookups`).
 *
 * Separado de `tickets.api.ts` porque o vocabulário não é o chamado: são as
 * mesmas rotas que qualquer outra tela usará para traduzir identificador em
 * nome, e misturá-las com as do `TicketsController` faria parecer que
 * pertencem a ele.
 *
 * Os tipos vêm de `@central/contracts` — a API e o front leem a mesma
 * declaração, e um campo que mude de forma quebra a compilação em vez de
 * aparecer como `undefined` na tela.
 */

const RAIZ = '/lookups';

/** Teto de ids por chamada, igual ao `ArrayMaxSize` do DTO. */
export const MAXIMO_DE_IDS_POR_CHAMADA = 200;

export function obterOpcoesDeFormulario(): Promise<TicketFormOptions> {
  return get<TicketFormOptions>(`${RAIZ}/ticket-form`);
}

/**
 * Resolve ids em nomes.
 *
 * Quem não resolver — pessoa de outro tenant, apagada — simplesmente não volta
 * na lista: a resposta não confirma a existência do registro. Cabe a quem
 * chama tratar a ausência, e não presumir que o resultado tem o mesmo tamanho
 * da entrada.
 */
export async function resolverPessoas(ids: readonly string[]): Promise<PersonRef[]> {
  if (ids.length === 0) return [];
  const consulta = new URLSearchParams({ ids: ids.join(',') });
  const resposta = await get<PeopleResponse>(`${RAIZ}/people?${consulta.toString()}`);
  return resposta.items;
}

/** Busca do campo de atribuição. O servidor exige pelo menos 2 caracteres. */
export async function buscarPessoas(termo: string, limite = 20): Promise<PersonRef[]> {
  const consulta = new URLSearchParams({ q: termo, limit: String(limite) });
  const resposta = await get<PeopleResponse>(`${RAIZ}/people/search?${consulta.toString()}`);
  return resposta.items;
}

/** Busca contatos externos para o campo de solicitante. */
export async function buscarContatos(
  termo: string,
  organizationId?: string,
  limite = 20,
): Promise<PersonRef[]> {
  const params: Record<string, string> = { q: termo, limit: String(limite) };
  if (organizationId) params['organizationId'] = organizationId;
  const consulta = new URLSearchParams(params);
  const resposta = await get<PeopleResponse>(`${RAIZ}/contacts/search?${consulta.toString()}`);
  return resposta.items;
}

/** Serviços disponíveis para uma organização a partir do contrato vigente. */
export async function buscarServicosParaOrg(organizationId: string): Promise<ServiceForOrgRef[]> {
  const params = new URLSearchParams({ organizationId });
  const resposta = await get<ServicesForOrgResponse>(`${RAIZ}/services?${params.toString()}`);
  return resposta.items;
}

// ── chaves do TanStack Query ───────────────────────────────────────────────

/**
 * A chave de pessoa é **por id**, não por lote.
 *
 * É o que faz a mesma pessoa aparecer na lista e no detalhe sem ser buscada
 * duas vezes: o lote é detalhe de transporte (ver `pessoas.tsx`), o cache é
 * por registro.
 */
export const chavesDeApoio = {
  raiz: ['apoio'] as const,
  formulario: ['apoio', 'formulario-de-chamado'] as const,
  pessoa: (id: string) => ['apoio', 'pessoa', id] as const,
  buscaDePessoas: (termo: string) => ['apoio', 'busca-de-pessoas', termo] as const,
  buscaDeContatos: (termo: string, orgId?: string) =>
    ['apoio', 'busca-de-contatos', orgId ?? '', termo] as const,
  servicosParaOrg: (orgId: string) => ['apoio', 'servicos-para-org', orgId] as const,
};
